/**
 * Which failure modes the cluster map draws by default (design doc 015 §6).
 *
 * A long-tailed registry (a handful of large modes + a long tail of tiny ones)
 * turns the map into mud: ~130 translucent convex hulls drawn on top of each
 * other. So the map starts with only the largest few modes shown and the legend
 * lets the user toggle the rest on. This is a *display* filter only — every mode
 * still lives in the registry and the legend; nothing about discovery changes.
 */

import type { FailureMode } from "../types";

/** Modes shown on the map by default — the long tail is hidden to cut overlap. */
export const DEFAULT_VISIBLE_COUNT = 5;

/**
 * The ids of the `n` largest modes by in-window size. Ties are broken by id so
 * the pick is deterministic (two equal-sized modes always resolve the same way).
 * Returns every id when there are `n` or fewer modes; `n <= 0` returns an empty
 * set.
 */
export function topModeIds(modes: FailureMode[], n: number): Set<string> {
  return new Set(
    [...modes]
      .sort(
        (a, b) =>
          b.size_in_window - a.size_in_window ||
          a.failure_mode_id.localeCompare(b.failure_mode_id),
      )
      .slice(0, Math.max(0, n))
      .map((m) => m.failure_mode_id),
  );
}
